import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import styles from './style/Device.module.css';

interface Device {
  id_device: number;
  password?: string;
  battery_status: number | string;
  battery_limit: number | string;
  level: number | string;
  level_limit: number | string;
  temperature?: number | string;
  time_last_update: string;
  latitude: number;
  longitude: number;
  affiliation: boolean;
  operability?: boolean;
  apn?: string;
}

type ActionModalState = {
  open: boolean;
  device: Device | null;
};

const Devices: React.FC = () => {
  const location = useLocation();

  const tenant = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tenant')?.trim() || 'tenant_a';
  }, [location.search]);

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionModal, setActionModal] = useState<ActionModalState>({
    open: false,
    device: null,
  });

  const fetchDevices = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);

      const res = await axios.get('/api/devices', {
        headers: {
          'X-Tenant-Key': tenant,
        },
        withCredentials: true,
      });

      const data = Array.isArray(res.data?.devices)
        ? res.data.devices
        : Array.isArray(res.data)
          ? res.data
          : [];

      setDevices(data);
    } catch (err) {
      console.error('Error fetching devices:', err);
      setDevices([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(true);

    const intervalId = window.setInterval(() => {
      fetchDevices(false);
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [tenant]);

  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => a.id_device - b.id_device);
  }, [devices]);

  const toNumber = (value: number | string | undefined) => {
    if (value === undefined || value === null || value === '') return NaN;
    return typeof value === 'number' ? value : parseFloat(String(value));
  };

  const formatDateTime = (value: string) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString('it-IT');
  };

  const getBatteryState = (device: Device) => {
    const battery = toNumber(device.battery_status);
    const limit = toNumber(device.battery_limit);

    if (isNaN(battery) || isNaN(limit)) {
      return { text: '—', status: 'neutral' as const };
    }

    return battery <= limit
      ? { text: `Low (${battery})`, status: 'danger' as const }
      : { text: `Ok (${battery})`, status: 'ok' as const };
  };

  const getLevelState = (device: Device) => {
    const level = toNumber(device.level);
    const limit = toNumber(device.level_limit);

    if (isNaN(level) || isNaN(limit)) {
      return { text: '—', status: 'neutral' as const };
    }

    return level <= limit
      ? { text: `Low (${level})`, status: 'danger' as const }
      : { text: `Ok (${level})`, status: 'ok' as const };
  };

  const getConnectionState = (device: Device) => {
    if (!device.time_last_update) {
      return { text: 'Unknown', status: 'neutral' as const };
    }

    const lastUpdate = new Date(device.time_last_update);
    if (isNaN(lastUpdate.getTime())) {
      return { text: 'Unknown', status: 'neutral' as const };
    }

    const diffInDays =
      (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24);

    return diffInDays >= 2
      ? { text: 'Disconnected', status: 'danger' as const }
      : { text: 'Connected', status: 'ok' as const };
  };

  const openManageModal = (device: Device) => {
    setActionModal({
      open: true,
      device,
    });
  };

  const closeManageModal = () => {
    setActionModal({
      open: false,
      device: null,
    });
  };

  const handleSetAffiliation = async (device: Device, value: boolean) => {
    try {
      await axios.put(
        `/api/devices/${device.id_device}/affiliation`,
        {
          affiliation: value,
        },
        {
          headers: {
            'X-Tenant-Key': tenant,
          },
          withCredentials: true,
        }
      );

      closeManageModal();
      await fetchDevices(false);
    } catch (err) {
      console.error('Errore aggiornamento affiliation:', err);
      alert("Errore durante l'aggiornamento dell'affiliazione.");
    }
  };

  const handleDeleteDevice = async (device: Device) => {
    const confirmed = window.confirm(
      `Sei sicuro di voler eliminare il device ${device.id_device}? Questa operazione non è reversibile.`
    );

    if (!confirmed) return;

    try {
      await axios.delete(`/api/devices/${device.id_device}`, {
        headers: {
          'X-Tenant-Key': tenant,
        },
        withCredentials: true,
      });

      closeManageModal();
      await fetchDevices(false);
    } catch (err) {
      console.error('Errore eliminazione device:', err);
      alert("Errore durante l'eliminazione del device.");
    }
  };

  const modalDevice = actionModal.device;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>Devices</h1>
          <p className={styles.subtitle}>
            Device Management : Operative State, Affiliation.
          </p>

        </div>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID Device</th>
                <th>Affiliation</th>
                <th>Operability</th>
                <th>Battery</th>
                <th>Level</th>
                <th>Temperature</th>
                <th>Connection</th>
                <th>Last Update</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>APN</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {sortedDevices.length > 0 ? (
                sortedDevices.map((device) => {
                  const isAffiliated = device.affiliation === true;

                  if (!isAffiliated) {
                    return (
                      <tr key={device.id_device}>
                        <td className={styles.idCell}>#{device.id_device}</td>

                        <td colSpan={10} className={styles.notAffiliatedCell}>
                          Not affiliated
                        </td>

                        <td>
                          <button
                            className={styles.manageBtn}
                            onClick={() => openManageModal(device)}
                          >
                            Actions
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  const batteryState = getBatteryState(device);
                  const levelState = getLevelState(device);
                  const connectionState = getConnectionState(device);

                  return (
                    <tr key={device.id_device}>
                      <td className={styles.idCell}>#{device.id_device}</td>

                      <td>
                        <span
                          className={`${styles.badge} ${
                            device.affiliation ? styles.badgeOk : styles.badgeOff
                          }`}
                        >
                          {device.affiliation ? 'Affiliated' : 'Not affiliated'}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`${styles.badge} ${
                            device.operability ? styles.badgeOk : styles.badgeNeutral
                          }`}
                        >
                          {device.operability ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`${styles.badge} ${
                            batteryState.status === 'danger'
                              ? styles.badgeDanger
                              : batteryState.status === 'ok'
                                ? styles.badgeOk
                                : styles.badgeNeutral
                          }`}
                        >
                          {batteryState.text}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`${styles.badge} ${
                            levelState.status === 'danger'
                              ? styles.badgeDanger
                              : levelState.status === 'ok'
                                ? styles.badgeOk
                                : styles.badgeNeutral
                          }`}
                        >
                          {levelState.text}
                        </span>
                      </td>

                      <td>{device.temperature ?? '—'}</td>

                      <td>
                        <span
                          className={`${styles.badge} ${
                            connectionState.status === 'danger'
                              ? styles.badgeDanger
                              : connectionState.status === 'ok'
                                ? styles.badgeOk
                                : styles.badgeNeutral
                          }`}
                        >
                          {connectionState.text}
                        </span>
                      </td>

                      <td>{formatDateTime(device.time_last_update)}</td>
                      <td>{device.latitude ?? '—'}</td>
                      <td>{device.longitude ?? '—'}</td>
                      <td>{device.apn || '—'}</td>

                      <td>
                        <button
                          className={styles.manageBtn}
                          onClick={() => openManageModal(device)}
                        >
                          Actions
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={12} className={styles.emptyRow}>
                    {loading ? 'Caricamento dispositivi...' : 'Nessun device trovato.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {actionModal.open && modalDevice && (
        <div className={styles.modalOverlay} onClick={closeManageModal}>
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>
                  Manage device #{modalDevice.id_device}
                </h2>
                <p className={styles.modalSubtitle}>
                  Choose an option to apply on this Device.
                </p>
              </div>

              <button className={styles.closeBtn} onClick={closeManageModal}>
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.infoGrid}>
                <div>
                  <span className={styles.infoLabel}>Affiliation</span>
                  <span className={styles.infoValue}>
                    {modalDevice.affiliation ? 'True' : 'False'}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Operability</span>
                  <span className={styles.infoValue}>
                    {modalDevice.operability ? 'True' : 'False'}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Battery</span>
                  <span className={styles.infoValue}>
                    {modalDevice.battery_status ?? '—'}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Level</span>
                  <span className={styles.infoValue}>
                    {modalDevice.level ?? '—'}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Last update</span>
                  <span className={styles.infoValue}>
                    {formatDateTime(modalDevice.time_last_update)}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Coordinates</span>
                  <span className={styles.infoValue}>
                    {modalDevice.latitude}, {modalDevice.longitude}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>APN</span>
                  <span className={styles.infoValue}>
                    {modalDevice.apn || '—'}
                  </span>
                </div>

                <div>
                  <span className={styles.infoLabel}>Temperature</span>
                  <span className={styles.infoValue}>
                    {modalDevice.temperature ?? '—'}
                  </span>
                </div>
              </div>

              <div className={styles.actionGroup}>
                <button
                  className={`${styles.actionBtn} ${styles.primaryBtn}`}
                  onClick={() => handleSetAffiliation(modalDevice, true)}
                >
                  Able Device
                </button>

                <button
                  className={`${styles.actionBtn} ${styles.secondaryBtn}`}
                  onClick={() => handleSetAffiliation(modalDevice, false)}
                >
                  Disable Affiliation
                </button>

                <button
                  className={`${styles.actionBtn} ${styles.dangerBtn}`}
                  onClick={() => handleDeleteDevice(modalDevice)}
                >
                  Delete Device
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;